import { Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, FormControl, Stack } from "@mui/material";
import { NoAgreementTemplateFileAvailable } from "@src/appV2/Agreements/NoAgreementTemplateFileAvailable";
import { type Agreement, type SignedAgreement } from "@src/appV2/Agreements/types";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { logError } from "@src/appV2/lib/analytics";
import { useToast } from "@src/appV2/lib/Notifications/Toasts/useToast";
import { PdfViewer } from "@src/appV2/PdfViewer/PdfViewer";
import { PdfViewerLoadingSkeleton } from "@src/appV2/PdfViewer/PdfViewerLoadingSkeleton";
import { type Worker } from "@src/appV2/Worker/api/types";

import { LoadingButton } from "../components/LoadingButton";
import { PageFooterWrapper } from "../components/PageFooterWrapper";
import { PageContent } from "../WorkerShifts/components/PageContent";
import { useICAgreementForm } from "./hooks/useICAgreementForm";
import { ICAgreementBanners } from "./ICAgreementBanners";
import { ICAgreementConfirmationCheckbox } from "./ICAgreementConfirmationCheckbox";

interface ICAgreementFormV2Props {
  worker: Worker;
  agreementToSign: Agreement | SignedAgreement | undefined;
  agreementsStatus: "loading" | "error" | "success";
  onSignAgreement: ({
    agreementToSign,
  }: {
    agreementToSign: Agreement | SignedAgreement;
  }) => Promise<void>;
  isSigningAgreement: boolean;
  title?: React.ReactNode;
}

export function ICAgreementFormV2(props: ICAgreementFormV2Props) {
  const { worker, agreementsStatus, onSignAgreement, isSigningAgreement, agreementToSign, title } =
    props;

  const { showErrorToast } = useToast();

  const { formMethods } = useICAgreementForm();

  const {
    formState: { errors, isValid: formIsValid, isSubmitted: isFormSubmitted },
    control,
    register,
    handleSubmit,
  } = formMethods;

  return (
    <>
      <PageContent>
        {title}
        <ICAgreementBanners
          worker={worker}
          showAgreementScrolledErrorBanner={isFormSubmitted && Boolean(errors.agreementScrolled)}
          showConfirmationErrorBanner={isFormSubmitted && Boolean(errors.confirmation)}
        />

        <Box sx={{ flex: "1 1 auto", overflow: "auto", marginTop: 6 }}>
          {agreementsStatus === "loading" && <PdfViewerLoadingSkeleton />}
          {agreementsStatus === "error" && <NoAgreementTemplateFileAvailable />}
          {agreementsStatus === "success" && <PdfViewer />}
        </Box>
        <FormControl component="fieldset" variant="standard">
          <input type="hidden" {...register("agreementScrolled")} />
        </FormControl>
      </PageContent>
      <PageFooterWrapper>
        <Stack spacing={4}>
          <Text variant="body2" color="error">
            You must read the full agreement by scrolling to the bottom. Click the checkbox to
            confirm and continue.
          </Text>

          <ICAgreementConfirmationCheckbox worker={worker} control={control} />

          <LoadingButton
            fullWidth
            size="large"
            variant={formIsValid ? "contained" : "outlined"}
            disabled={agreementsStatus === "loading"}
            isLoading={agreementsStatus === "loading" || isSigningAgreement}
            data-testid="sign-agreement-button"
            sx={{ lineHeight: "1.5rem" }}
            onClick={handleSubmit(async () => {
              if (!isDefined(agreementToSign)) {
                logError(APP_V2_APP_EVENTS.AGREEMENT_TO_SIGN_NOT_DEFINED, {
                  error: new Error("Agreement to sign is not defined"),
                  metadata: {
                    workerId: worker.userId,
                    workerName: worker.name,
                  },
                });
                showErrorToast(
                  "Unable to sign the agreement. Please try again or contact support."
                );
                return;
              }

              await onSignAgreement({
                agreementToSign,
              });
            })}
          >
            I have read and agree to this Independent Contractor Agreement
          </LoadingButton>
        </Stack>
      </PageFooterWrapper>
    </>
  );
}
